/* currentUnloadHandler is null unless there is unsaved data being watched. */
import {
  currentUnloadHandler,
  hasUnsavedChanges,
  unregisterUnloadHandler,
  getDirtyMessage,
  registerUnloadHandler,
} from "./warn_unsaved_changes.js";

export const NAVIGATION_CANCELLED_EVENT = "unsaved-changes:navigation-cancelled";

let globalLinkInterceptorActive = false;
let navigationHandlerActive = false;
let suppressNavigationPromptUntil = 0;
let exemptNavigationUntil = 0;

const triggerSelectors = [
  /* Can mark specific elements... */
  "a[data-warn-unsaved-trigger]",
  "button[data-warn-unsaved-trigger]",
  "form[data-warn-unsaved-trigger]",

  /* or a containing element */
  "[data-warn-unsaved-trigger] a[data-remote]",
  "[data-warn-unsaved-trigger] button[data-remote]",
  "[data-warn-unsaved-trigger] form[data-remote]",
].join(", ");

const confirmAndNavigate = (destinationHref) => {
  // CRITICAL: We use window.confirm (native browser dialog) instead of our nice Confirm.confirm modal
  // because window.confirm is synchronous and blocks ALL JavaScript execution including navigation.
  // Our async modal would allow navigation to proceed while waiting for user response.
  // This is a UX trade-off for reliability - the native dialog ensures navigation is truly blocked.
  const result = window.confirm(getDirtyMessage());

  if (result) {
    unregisterUnloadHandler();
    suppressNavigationPromptUntil = performance.now() + 1500;
    if (destinationHref) {
      window.location.href = destinationHref;
    }
    return true;
  }

  document.dispatchEvent(new CustomEvent(NAVIGATION_CANCELLED_EVENT));
  return false;
};

export const promptForUnsavedChanges = () => {
  return confirmAndNavigate(null);
};

export const warnUnsavedTrigger = (ev) => {
  if (currentUnloadHandler) {
    const result = promptForUnsavedChanges();
    if (result) return true;

    ev.stopPropagation();
    ev.preventDefault();

    document.dispatchEvent(new CustomEvent(NAVIGATION_CANCELLED_EVENT));

    return false;
  }

  return true;
};

// BEGIN: additional, more-thorough handling, added during Inventory modernization,
// because I noticed a lot was slipping by the existing approach to listening.

const isInterceptableLink = (link, ev) => {
  if (!link) return false;
  // Ignore modifier/middle clicks and external targets
  if (link.target === "_blank" || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey)
    return false;
  // Allow hash-only updates to pass (no data loss risk). This includes bare href="#"
  // links, whose click handlers do the real work and call preventDefault.
  if (link.getAttribute("href")?.startsWith("#")) return false;
  try {
    const u = new URL(link.href, window.location.origin);
    if (u.pathname === window.location.pathname && u.hash && !u.search) return false;
  } catch (err) {
    console.error(err);
  }
  // Don't intercept at the global level if:
  // - data-no-warn-unsaved: User explicitly opted out of warnings
  // - data-warn-unsaved-trigger: Has its own specific handler from scan()
  if (
    link.hasAttribute("data-no-warn-unsaved") ||
    link.hasAttribute("data-warn-unsaved-trigger") ||
    link.closest("[data-warn-unsaved-trigger]")
  ) {
    return false;
  }
  return true;
};

const handleExemption = () => {
  exemptNavigationUntil = performance.now() + 100;
  unregisterUnloadHandler();
  setTimeout(() => {
    if (hasUnsavedChanges()) {
      registerUnloadHandler();
    }
  }, 100);
};

const absorbClickEvent = (event, link) => {
  const absorb = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  };
  absorb(event);
  if (link) {
    link.addEventListener("click", (ev) => absorb(ev), { capture: true, once: true });
  }
};

// Prevent multiple prompts from overlapping events (pointerdown + mousedown + touchstart)
let isPrompting = false;

const handleLinkInterception = (ev, link) => {
  if (isPrompting || !hasUnsavedChanges()) return;

  if (!isInterceptableLink(link, ev)) {
    // Handle data-no-warn-unsaved exemptions
    if (link && link.hasAttribute("data-no-warn-unsaved")) {
      handleExemption();
    }
    return;
  }

  const destinationHref = link.href;

  // We returned early if !hasUnsavedChanges(), so otherwise we know
  // we should be killing the gesture and preventing navigation
  absorbClickEvent(ev, link);

  // Prompt synchronously and navigate if confirmed
  isPrompting = true;
  confirmAndNavigate(destinationHref);
  isPrompting = false;
};

// Global link interceptor (non-Navigation API path, in case Navigation API is not supported
// and just overall to "make sure" we can block navigation - it can be tricky to intercept navigation)
const setupGlobalLinkInterceptor = () => {
  if (globalLinkInterceptorActive) {
    return;
  }

  globalLinkInterceptorActive = true;

  // Intercept earliest user gesture: pointerdown (widest coverage). Fallback to mousedown.
  const pointerHandler = (ev) => {
    const link = ev.target.closest("a[href]");
    handleLinkInterception(ev, link);
  };

  // Keyboard activation of links (Enter/Space)
  const keyHandler = (ev) => {
    if (!(ev.key === "Enter" || ev.key === " ")) return;
    const link = ev.target.closest("a[href]");
    handleLinkInterception(ev, link);
  };

  // Use pointerdown for modern browsers (including mobile touch)
  document.addEventListener("pointerdown", pointerHandler, true);
  // Fallback for older browsers that don't support pointer events
  document.addEventListener("mousedown", pointerHandler, true);
  // Add touchstart for legacy iOS Safari < 13.4
  document.addEventListener("touchstart", pointerHandler, true);
  // Keyboard activation
  document.addEventListener("keydown", keyHandler, true);
};

// Navigation API support (Chrome/Edge 102+, not yet in Firefox/Safari)
const setupNavigationAPI = () => {
  if (!("navigation" in window)) {
    return false;
  }

  window.navigation.addEventListener("navigate", async (navigateEvent) => {
    if (navigateEvent.hashChange) return;

    // Check if this navigation is exempt (e.g., from data-no-warn-unsaved link)
    if (performance.now() < exemptNavigationUntil) {
      return;
    }

    // If another path already confirmed (e.g., trigger click handler), skip prompting here
    if (performance.now() < suppressNavigationPromptUntil) {
      return;
    }
    if (hasUnsavedChanges() && navigateEvent.canIntercept) {
      // Temporarily unregister beforeunload to prevent double confirmation
      const hadUnloadHandler = currentUnloadHandler !== null;
      if (hadUnloadHandler) {
        unregisterUnloadHandler();
      }

      // Using synchronous window.confirm here too for consistency
      const result = confirmAndNavigate(null);

      if (!result) {
        // User cancelled - restore unload handler and prevent navigation
        if (hadUnloadHandler) {
          registerUnloadHandler();
        }
        navigateEvent.preventDefault();
      }
    }
  });

  return true;
};

const initializeNavigationHandler = () => {
  if (navigationHandlerActive) {
    return;
  }

  navigationHandlerActive = true;
  setupNavigationAPI();
};

// END: additional, more-thorough handling (see note on BEGIN).

const scan = (document) => {
  setupGlobalLinkInterceptor();
  initializeNavigationHandler();

  const triggers = document.querySelectorAll(triggerSelectors);

  triggers.forEach((element) => {
    if (element._warnUnsavedTrigger) {
      return;
    }
    element._warnUnsavedTrigger = true;
    element.setAttribute("warn-unsaved-trigger-marked", true);

    const eventName = element.nodeName === "FORM" ? "submit" : "click";
    element.addEventListener(eventName, (ev) => warnUnsavedTrigger(ev), {
      capture: true,
    });
  });
};

document.addEventListener("DOMContentLoaded", () => scan(document));
document.addEventListener("partial:replace", (ev) => scan(ev.container));
